/**
 * Mode Selector Component
 * Entry screen for choosing game mode
 */

import { GameMode } from '../core/GameEngine';
import { 
  isTutorialCompleted, 
  isChaosTutorialCompleted, 
  isRhythmTutorialCompleted 
} from '../utils/storage';

interface ModeSelectorProps {
  onSelectMode: (mode: GameMode) => void;
  onStartTutorial?: (mode: GameMode) => void;
}

interface ModeOption {
  mode: GameMode;
  title: string;
  emoji: string;
  description: string;
  color: string;
  getTutorialCompleted: () => boolean;
}

const modes: ModeOption[] = [
  {
    mode: 'classic',
    title: 'Classic',
    emoji: '🎮',
    description: 'Pure Simon Says - memorize and repeat the sequence',
    color: 'var(--accent-g)',
    getTutorialCompleted: isTutorialCompleted
  },
  {
    mode: 'chaos',
    title: 'Chaos',
    emoji: '🌀',
    description: 'Moving buttons, decoy intruders, and invisible challenges',
    color: 'var(--accent-r)',
    getTutorialCompleted: isChaosTutorialCompleted
  },
  {
    mode: 'rhythm',
    title: 'Rhythm',
    emoji: '💃',
    description: 'Match the beat with precision timing and panic multipliers',
    color: 'var(--accent-y)',
    getTutorialCompleted: isRhythmTutorialCompleted
  }
];

export function ModeSelector({ onSelectMode, onStartTutorial }: ModeSelectorProps) {
  const handleKeyPress = (mode: GameMode, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelectMode(mode);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      width: '100%',
      padding: 'max(20px, env(safe-area-inset-top)) 20px max(20px, env(safe-area-inset-bottom))',
      gap: '32px',
      background: 'var(--bg)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        maxWidth: '420px'
      }}>
        <h1 style={{
          fontFamily: 'Syne, sans-serif',
          fontSize: 'clamp(32px, 8vw, 48px)',
          fontWeight: 800,
          letterSpacing: '-1px',
          color: 'var(--accent-b)',
          margin: 0,
          textAlign: 'center'
        }}>
          SIMON REVOLUTION
        </h1>
        <p style={{
          fontSize: '16px',
          color: 'var(--muted)',
          margin: 0,
          textAlign: 'center'
        }}>
          Choose your challenge
        </p>
      </div>

      {/* Mode Cards */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: '100%',
        maxWidth: '420px'
      }}>
        {modes.map((option) => (
          <button
            key={option.mode}
            onClick={() => onSelectMode(option.mode)}
            onKeyPress={(e) => handleKeyPress(option.mode, e)}
            tabIndex={0}
            aria-label={`Select ${option.title} mode`}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px',
              padding: '20px',
              background: 'var(--surface)',
              border: `2px solid var(--border)`,
              borderRadius: '16px',
              cursor: 'pointer',
              transition: 'all 200ms ease',
              outline: 'none',
              textAlign: 'left',
              width: '100%'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = option.color;
              e.currentTarget.style.boxShadow = `0 8px 24px ${option.color}40`;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = option.color;
              e.currentTarget.style.boxShadow = `0 0 0 3px ${option.color}40`;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {/* Emoji Icon */}
            <div style={{
              fontSize: '48px',
              lineHeight: 1,
              flexShrink: 0
            }}>
              {option.emoji}
            </div>

            {/* Text Content */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              flex: 1
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <h2 style={{
                  fontFamily: 'Syne, sans-serif',
                  fontSize: '24px',
                  fontWeight: 700,
                  color: option.color,
                  margin: 0
                }}>
                  {option.title}
                </h2>
                {option.getTutorialCompleted() && (
                  <span style={{
                    fontSize: '16px',
                    color: 'var(--accent-g)'
                  }}>
                    ✓
                  </span>
                )}
              </div>
              <p style={{
                fontSize: '14px',
                color: 'var(--muted)',
                margin: 0,
                lineHeight: 1.4
              }}>
                {option.description}
              </p>
              {onStartTutorial && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onStartTutorial(option.mode);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginTop: '8px',
                    padding: '6px 12px',
                    background: 'transparent',
                    border: `1px solid ${option.color}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: option.color,
                    transition: 'all 200ms ease',
                    outline: 'none',
                    width: 'fit-content'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = option.color;
                    e.currentTarget.style.color = 'var(--bg)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = option.color;
                  }}
                >
                  <span style={{ fontSize: '14px' }}>🎓</span>
                  Tutorial
                </button>
              )}
            </div>

            {/* Arrow Indicator */}
            <div style={{
              fontSize: '24px',
              color: 'var(--muted)',
              flexShrink: 0
            }}>
              →
            </div>
          </button>
        ))}
      </div>

      {/* Footer Hint */}
      <div style={{
        fontSize: '12px',
        color: 'var(--muted)',
        textAlign: 'center',
        maxWidth: '300px'
      }}>
        Use keyboard (Tab + Enter) or click to select
      </div>
    </div>
  );
}
